import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useSelector } from 'react-redux';

import { getConfig } from '../../config/config-helper';
import { useAppDispatch } from '../../hooks';
import { fetchSolution } from '../../reducers/solutionsSlice';
import ProjectIcon from '../common/ProjectIcon';
import { PageCenteredLayout } from '../layout/PageCenteredLayout';
import CopyUrlButton from '../controls/CopyUrlButton';
import { useSolution } from '../../hooks/useSolution';
import { useNavigationTab } from '../../hooks/useNavigationTab';
import { classNames } from '../../utilities/styleUtils';
import { useToken } from '../../hooks/useToken';
import SectionLayout from '../layout/SectionLayout';
import Grid3Layout from '../layout/Grid3Layout';
import Tag from '../common/Tag';
import MarkdownContent from '../common/MarkdownContent';
import { StarSolutionButton } from '../controls/StarSolutionButton';
import { UserIcon } from '@heroicons/react/16/solid';
import { PlusCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import DataOpsInfinity from '../../assets/images/icons/DataOps_darkblue_icon.svg';
import Button from '../controls/Button';
import TierTag from '../project/TierTag';
import { fetchSolutionDeployments, selectSolutionDeployments } from '../../reducers/solutionDeploymentsSlice';
import { fetchSnowflakeAccounts, selectAccounts } from '../../reducers/snowflakeAccountsSlice';
import SolutionDemoItem from '../project/SolutionDemoItem';

function TagsList({ label, tags }: { label: string; tags: string[] }) {
  return tags.length !== 0 ? (
    <div className="">
      <h3>{label}</h3>
      <div className="space-x-1">
        {tags.map((tag) => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </div>
    </div>
  ) : (
    <></>
  );
}

function EventSolutionPageSkeleton() {
  return (
    <main>
      <header className="relative isolate pt-10">
        <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute left-16 top-full -mt-16 transform-gpu opacity-50 blur-3xl xl:left-1/2 xl:-ml-80">
            <div
              style={{
                clipPath:
                  'polygon(100% 38.5%, 82.6% 100%, 60.2% 37.7%, 52.4% 32.1%, 47.5% 41.8%, 45.2% 65.6%, 27.5% 23.4%, 0.1% 35.3%, 17.9% 0%, 27.7% 23.4%, 76.2% 2.5%, 74.2% 56%, 100% 38.5%)',
              }}
              className={classNames(
                'aspect-[1154/678] w-[72.125rem] bg-gradient-to-br',
                'from-dataops-supporting-gray to-dataops-supporting-black',
              )}
            />
          </div>
          <div className="absolute inset-x-0 bottom-0 h-px bg-gray-900/5" />
        </div>

        <PageCenteredLayout>
          <div className="mx-auto flex max-w-2xl items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none">
            <div className="flex items-center gap-x-6">
              <ProjectIcon iconSrc={undefined} className="animate-pulse" />
              <h1>
                <div className="mt-1 w-60 h-6 rounded-lg text-base font-semibold bg-dataops-supporting-gray bg-shine"></div>
                <a
                  href={'solutionDolUrl'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 flex items-center space-x-1 text-sm/6 text-gray-500 hover:underline"
                >
                  <img src={DataOpsInfinity} className="w-4 h-4 mt-0.5 animate-pulse opacity-40" />
                  <div className="w-60 h-5 rounded-lg bg-dataops-supporting-gray bg-shine"></div>
                </a>
              </h1>
            </div>
            <div className="flex items-center sm:gap-x-1">
              <div className="w-20 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
              <div className="w-14 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
              <div className="w-20 h-6 rounded-md bg-dataops-supporting-gray bg-shine"></div>
            </div>
          </div>
        </PageCenteredLayout>
      </header>

      <PageCenteredLayout>
        <Grid3Layout>
          <SectionLayout
            skeleton
            label="Actions"
            className="bg-dataops-supporting-gray/40 md:col-span-1 md:col-start-3"
          >
            <div className="space-y-1"></div>
          </SectionLayout>
          <SectionLayout skeleton label="Deployments" className="md:col-span-1 md:col-start-3 md:row-start-2">
            <></>
          </SectionLayout>
          <SectionLayout skeleton label="Description" className="md:col-span-2 md:row-start-1">
            <div className="space-y-2">
              <p className="w-full h-4 rounded-lg bg-shine"></p>
              <p className="w-full h-4 rounded-lg bg-shine"></p>
              <p className="w-1/2 h-4 rounded-lg bg-shine"></p>
            </div>
          </SectionLayout>
          <SectionLayout skeleton label="Tags" className="md:col-span-2 md:row-start-2">
            <div className="grid md:grid-cols-2 gap-x-2 gap-y-4 text-sm/6 text-gray-500">
              <div className="flex space-x-1">
                <div className="w-20 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
                <div className="w-14 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
              </div>
            </div>
          </SectionLayout>
        </Grid3Layout>
      </PageCenteredLayout>
    </main>
  );
}

export default function EventSolutionPage() {
  const { dataopsliveBaseUrl } = getConfig();
  const { canPublish } = useCurrentUser();

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const token = useToken();
  const { solution } = useSolution(id);
  const fullPath = `/event-solutions/${id ?? ''}`;
  const solutionDolUrl = `${dataopsliveBaseUrl}/${solution?.fullPath ?? ''}`;
  const avatarUrl = solution?.avatarUrl ?? solution?.listing?.icon;

  // Solution deployments (new demos)
  const solutionDeployments = useSelector(selectSolutionDeployments);
  const snowflakeAccounts = useSelector(selectAccounts);

  // Run once to get the solution details
  useEffect(() => {
    dispatch(fetchSolution({ token, fullPath, iid: id })).catch((err) => {
      console.log('Fetch solution error:', err);
    });
  }, []);

  // Fetch solution deployments and snowflake accounts
  useEffect(() => {
    if (token) {
      dispatch(fetchSolutionDeployments(token)).catch((err) => {
        console.error('Failed to fetch solution deployments:', err);
      });
      dispatch(fetchSnowflakeAccounts(token)).catch((err) => {
        console.error('Failed to fetch snowflake accounts:', err);
      });
    }
  }, [token, dispatch]);

  useNavigationTab(
    solution?.listing?.name
      ? {
          section: 'discover/event-solutions',
          label: `${solution?.listing?.name}`,
          href: fullPath,
          status: solution?.listing?.name ? 'loaded' : 'loading',
        }
      : null,
    [solution?.listing?.name, token],
  );

  // Filter solution deployments for this specific solution
  const filteredSolutionDeployments = useMemo(() => {
    return solutionDeployments.filter((deployment) => {
      return deployment.project_id.toString() === id;
    });
  }, [solutionDeployments, id]);

  // Helper function to get account name
  const getAccountName = (accountId: number) => {
    const account = snowflakeAccounts.find((acc) => acc.id === accountId);
    return account?.account ?? `Account ID: ${accountId}`;
  };

  if (solution?.listing?.name === undefined || solution?.listing?.name === '') {
    setTimeout(() => {
      window.location.reload();
    }, 3000);
    return <EventSolutionPageSkeleton />;
  }

  return (
    <>
      <main>
        <header className="relative isolate pt-10">
          <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
            <div className="absolute left-16 top-full -mt-16 transform-gpu opacity-50 blur-3xl xl:left-1/2 xl:-ml-80">
              <div
                style={{
                  clipPath:
                    'polygon(100% 38.5%, 82.6% 100%, 60.2% 37.7%, 52.4% 32.1%, 47.5% 41.8%, 45.2% 65.6%, 27.5% 23.4%, 0.1% 35.3%, 17.9% 0%, 27.7% 23.4%, 76.2% 2.5%, 74.2% 56%, 100% 38.5%)',
                }}
                className={classNames(
                  'aspect-[1154/678] w-[72.125rem] bg-gradient-to-br',
                  'from-dataops-primary-light-blue to-dataops-secondary-dark-blue',
                )}
              />
            </div>
            <div className="absolute inset-x-0 bottom-0 h-px bg-gray-900/5" />
          </div>

          <PageCenteredLayout>
            <div className="mx-auto flex items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none">
              <div className="flex items-center gap-x-6">
                <ProjectIcon iconSrc={avatarUrl} />
                <h1>
                  <div className="mt-1 text-base font-semibold text-dataops-primary-dark-blue">
                    {solution?.listing?.name}
                  </div>
                  <a
                    href={solutionDolUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 text-sm/6 text-gray-500 hover:underline"
                  >
                    <img src={DataOpsInfinity} className="w-4 h-4 mt-0.5" />
                    <span className="truncate text-ellipsis w-3/4">{solution.fullPath}</span>
                  </a>
                </h1>
              </div>
              <div className="flex flex-col items-end gap-y-2 sm:gap-x-1">
                <div className="flex gap-x-1">
                  <StarSolutionButton solution={solution} />
                  <CopyUrlButton view="solution" />
                </div>
                <div className="flex gap-x-1">
                  <TierTag tier={solution?.listing?.tier} hol={true} />
                  <Tag color={'lightGray'}>
                    <UserIcon className="w-3 h-3 mr-1" />
                    <span>{solution?.listing?.creator}</span>
                  </Tag>
                </div>
              </div>
            </div>
          </PageCenteredLayout>
        </header>

        <PageCenteredLayout>
          <Grid3Layout>
            <SectionLayout label="Actions" className="bg-dataops-supporting-gray/40 md:col-span-1 md:col-start-3">
              <div className="space-y-1">
                <div className="flex flex-col space-y-1">
                  <Button
                    intent="primary"
                    size="large"
                    onClick={() => {
                      if (id) navigate(`/new/deployment?solution=${id}`);
                    }}
                  >
                    <RocketLaunchIcon className="w-4 h-4 mr-1" /> Deploy
                  </Button>
                  {canPublish && (
                    <Button
                      intent="primary"
                      size="large"
                      onClick={() => {
                        if (id) navigate(`/new/event?solution=${id}`);
                      }}
                    >
                      <PlusCircleIcon className="w-4 h-4 mr-1" /> Create Event
                    </Button>
                  )}
                </div>
              </div>
            </SectionLayout>
            <SectionLayout label="Deployments" className="md:col-span-1 md:col-start-3 md:row-start-2">
              {filteredSolutionDeployments.length === 0 ? (
                <div className="text-sm/6 text-gray-500">No deployments yet</div>
              ) : (
                <div className="flex flex-col h-full">
                  <div className="overflow-y-auto max-h-64 flex-1">
                    <ul role="list" className="w-full space-y-1">
                      {filteredSolutionDeployments.map((deployment) => (
                        <li key={deployment.id}>
                          <SolutionDemoItem
                            deployment={deployment}
                            accountName={getAccountName(deployment.snowflake_account_id)}
                            mini={true}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </SectionLayout>
            <SectionLayout label="Description" className="md:col-span-2 md:row-start-1">
              <p className="text-sm/6">{solution?.listing?.description}</p>
            </SectionLayout>
            <SectionLayout label="Tags" className="md:col-span-2 md:row-start-2">
              <div className="grid md:grid-cols-2 gap-x-2 gap-y-4 text-sm/6 text-gray-500">
                <TagsList label="Features" tags={solution?.listing?.snowflake_feature ?? []} />
                <TagsList label="Verticals" tags={solution?.listing?.vertical_applicability ?? []} />
                <TagsList label="Use Cases" tags={solution?.listing?.use_case ?? []} />
                <TagsList label="Workload" tags={solution?.listing?.workload ?? []} />
                <TagsList label="Partners" tags={solution?.listing?.partner ?? []} />
                <TagsList label="Events" tags={solution?.listing?.event ?? []} />
              </div>
            </SectionLayout>
            {solution?.listing?.preview && (
              <SectionLayout label="Learn more" className="md:col-span-3">
                <MarkdownContent markdown={solution?.listing?.preview} />
              </SectionLayout>
            )}
            {solution?.listing?.help && (
              <SectionLayout label="Help" className="md:col-span-3">
                <MarkdownContent markdown={solution?.listing?.help} />
              </SectionLayout>
            )}
          </Grid3Layout>
        </PageCenteredLayout>
      </main>
    </>
  );
}
